import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { generateEssayDraft } from "@/lib/smartessay.functions";
import type { Outline } from "@/lib/smartessay-types";
import { disclaimerPdfFooter } from "@/lib/disclaimers";
import {
  AcademicConsentDialog,
  useAcademicConsent,
} from "@/components/AcademicConsentGate";

type Format = "MLA" | "APA";

export function EssayPdfButton({
  outline,
  title,
  prompt,
  assignmentType,
  level,
  tone,
  wordMin,
  wordMax,
}: {
  outline: Outline;
  title: string;
  prompt?: string;
  assignmentType: "essay" | "summary" | "key_concepts";
  level: "intermediate" | "advanced";
  tone: "academic" | "natural";
  wordMin: number;
  wordMax: number;
}) {
  const draft = useServerFn(generateEssayDraft);
  const [format, setFormat] = useState<Format>("MLA");
  const [loading, setLoading] = useState(false);
  const consent = useAcademicConsent();

  function onClickDownload() {
    consent.requireConsent(() => {
      void downloadPdf();
    });
  }

  async function downloadPdf() {
    setLoading(true);
    try {
      const essay = await draft({
        data: {
          title,
          prompt,
          assignment_type: assignmentType,
          outline,
          format,
          level,
          tone,
          word_min: wordMin,
          word_max: wordMax,
        },
      });

      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "in", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 1;
      const usableW = pageW - margin * 2;
      const lineHeight = 0.32; // double-spaced 12pt approx

      doc.setFont("times", "normal");
      doc.setFontSize(12);

      let y = margin;
      const ensureSpace = (needed = lineHeight) => {
        if (y + needed > pageH - margin) {
          doc.addPage();
          y = margin;
        }
      };
      // Parse "*italic*" markers into segments
      type Seg = { text: string; italic: boolean };
      const parseSegments = (text: string): Seg[] => {
        const out: Seg[] = [];
        const re = /\*([^*\n]+)\*/g;
        let last = 0;
        let m: RegExpExecArray | null;
        while ((m = re.exec(text)) !== null) {
          if (m.index > last) out.push({ text: text.slice(last, m.index), italic: false });
          out.push({ text: m[1], italic: true });
          last = m.index + m[0].length;
        }
        if (last < text.length) out.push({ text: text.slice(last), italic: false });
        return out.length ? out : [{ text, italic: false }];
      };

      // Word-wrap rich segments and render with mixed italic/normal.
      const renderRich = (text: string, firstIndent: number, hangingIndent: number) => {
        const segs = parseSegments(text);
        type Tok = { text: string; italic: boolean; space: boolean };
        const tokens: Tok[] = [];
        for (const s of segs) {
          const parts = s.text.split(/(\s+)/);
          for (const p of parts) {
            if (!p) continue;
            tokens.push({ text: p, italic: s.italic, space: /^\s+$/.test(p) });
          }
        }
        const measure = (t: Tok) => {
          doc.setFont("times", t.italic ? "italic" : "normal");
          return doc.getTextWidth(t.text);
        };
        let x = margin + firstIndent;
        let maxW = usableW - firstIndent;
        let lineToks: Tok[] = [];
        let lineW = 0;
        const flush = (isLast: boolean) => {
          ensureSpace();
          let cx = x;
          while (lineToks.length && lineToks[lineToks.length - 1].space) lineToks.pop();
          for (const t of lineToks) {
            doc.setFont("times", t.italic ? "italic" : "normal");
            doc.text(t.text, cx, y);
            cx += measure(t);
          }
          y += lineHeight;
          lineToks = [];
          lineW = 0;
          if (!isLast) {
            x = margin + hangingIndent;
            maxW = usableW - hangingIndent;
          }
        };
        for (const t of tokens) {
          const w = measure(t);
          if (lineW + w > maxW && lineToks.length) {
            flush(false);
            if (t.space) continue;
          }
          lineToks.push(t);
          lineW += w;
        }
        if (lineToks.length) flush(true);
        doc.setFont("times", "normal");
      };

      const writeLine = (text: string, opts?: { align?: "left" | "center"; bold?: boolean; indent?: number }) => {
        doc.setFont("times", opts?.bold ? "bold" : "normal");
        const align = opts?.align ?? "left";
        const indent = opts?.indent ?? 0;
        const wrapped = doc.splitTextToSize(text, usableW - indent);
        for (const ln of wrapped) {
          ensureSpace();
          if (align === "center") {
            doc.text(ln, pageW / 2, y, { align: "center" });
          } else {
            doc.text(ln, margin + indent, y);
          }
          y += lineHeight;
        }
      };

      // Header / author block
      if (format === "MLA") {
        for (const line of essay.author_block) writeLine(line);
        writeLine(essay.title, { align: "center" });
      } else {
        y = pageH / 3;
        writeLine(essay.title, { align: "center", bold: true });
        y += lineHeight;
        for (const line of essay.author_block) writeLine(line, { align: "center" });
        doc.addPage();
        y = margin;
        writeLine(essay.title, { align: "center", bold: true });
        y += lineHeight * 0.25;
      }

      // Body paragraphs with first-line indent + inline italics
      doc.setFont("times", "normal");
      for (let i = 0; i < essay.paragraphs.length; i++) {
        const para = essay.paragraphs[i];
        ensureSpace();
        renderRich(para, 0.5, 0);
        y += lineHeight * 0.5;
      }

      // Works Cited / References with hanging indent + italics
      doc.addPage();
      y = margin;
      writeLine(format === "MLA" ? "Works Cited" : "References", { align: "center", bold: true });
      y += lineHeight * 0.5;
      for (const entry of essay.works_cited) {
        renderRich(entry, 0, 0.5);
      }

      // APA 7 + MLA 9 both require a page number in the top-right of every page.
      // (APA 7 student papers: page number top-right starting on the title page.
      //  MLA 9: "Last Name #" top-right starting on page 1.)
      const totalPages = doc.getNumberOfPages();
      const lastName = (() => {
        const first = essay.author_block[0] ?? "";
        const parts = first.trim().split(/\s+/);
        return parts.length ? parts[parts.length - 1] : "";
      })();
      const footerText = disclaimerPdfFooter();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFont("times", "normal");
        doc.setFontSize(12);
        const label = format === "MLA" && lastName ? `${lastName} ${p}` : `${p}`;
        doc.text(label, pageW - margin, margin - 0.4, { align: "right" });

        // Academic-integrity footer on every page
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(footerText, pageW / 2, pageH - 0.4, { align: "center", maxWidth: usableW });
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
      }

      const safeName = title.replace(/[^a-z0-9]+/gi, "_").slice(0, 60) || "essay";
      doc.save(`${safeName}_${format}.pdf`);
      toast.success(`${format} essay downloaded`);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't build essay PDF.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-6 rounded-xl border border-border bg-background p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Finished essay PDF</div>
          <p className="mt-1 text-sm text-muted-foreground">
            Turn this outline into a complete, formatted draft you can download.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-border bg-card p-0.5">
            {(["MLA", "APA"] as const).map((f) => (
              <button
                key={f}
                type="button"
                disabled={loading || format === f}
                onClick={() => setFormat(f)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  format === f ? "bg-primary text-primary-foreground shadow-glow" : "text-muted-foreground hover:text-foreground"
                } disabled:cursor-not-allowed`}
              >
                {f}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={onClickDownload} disabled={loading}>
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Writing essay…</>
            ) : (
              <><FileDown className="mr-2 h-4 w-4" /> Download PDF</>
            )}
          </Button>
        </div>
      </div>
      <AcademicConsentDialog
        open={consent.open}
        onOpenChange={consent.setOpen}
        onAccept={consent.accept}
      />
    </div>
  );
}
